import { useCallback, useEffect, useState } from "react"

import { useEditor, useNode } from "@craftjs/core"

import { define, useCustomProp } from "@/components/builder/utils"
import { useParseSerialized } from "@/components/builder/utils/serialize-block"
import { useBuilderStore } from "@/components/builder/utils/store"

import BasePreview, { Action } from "./base";

function TemplateBlockPreview({ children }) {
  const { templateBlock: { loading } } = useBuilderStore()

  const { value: blockId } = useCustomProp('blockId')
  const { value: blockVersion } = useCustomProp('blockVersion')

  const sourceBlockVersion = useSourceBlockVersion(blockId)
  const handleUpdateBlockVersion = useUpdateBlockVersion(blockId)
  const hasUpdate = sourceBlockVersion && sourceBlockVersion !== blockVersion

  if (loading) {
    return <div className='p-8 flex items-center justify-center text-gray-500 outline-2 outline-dashed outline-gray-300 bg-gray-50/50 rounded-lg transition-all duration-200 hover:outline-gray-400 hover:bg-gray-100/50'>
      Loading...
    </div>
  }

  return <BasePreview customActions={<>
    {hasUpdate ? <Action onClick={handleUpdateBlockVersion}>
      There is a new version of the block, click to update.
    </Action> : null}
  </>}>
    {children}
  </BasePreview>
}

function useSourceBlockVersion(sourceBlockId) {
  const { templateBlock: { fetchBlockVersion } } = useBuilderStore()

  const [version, setVersion] = useState(null)

  const fetchVersion = useCallback(async sourceBlockId => {
    const version = await fetchBlockVersion(sourceBlockId) || null
    setVersion(version)
  }, [fetchBlockVersion])

  useEffect(() => {
    if (!version && sourceBlockId) {
      fetchVersion(sourceBlockId)
    }
  }, [version, sourceBlockId, fetchVersion])

  return version
}

function useUpdateBlockVersion(sourceBlockId) {
  const { templateBlock: { fetchBlockById } } = useBuilderStore()
  const { actions: { addNodeTree, setCustom, delete: deleteNode }, query } = useEditor()
  const { id: rootNodeId } = useNode()

  const parseSerialized = useParseSerialized()

  const handleClean = useCallback(() => {
    const nodes = [...query.node(rootNodeId).get().data.nodes]
    nodes.forEach(id => deleteNode(id))
  }, [rootNodeId, deleteNode, query])

  const handleUpdate = useCallback(async () => {
    const cmsBuilderBlock = await fetchBlockById(sourceBlockId)
    if (cmsBuilderBlock) {
      const { definition, meta } = cmsBuilderBlock

      if (definition) {
        handleClean()

        addNodeTree(parseSerialized(definition), rootNodeId, 0)
        setCustom(rootNodeId, custom => {
          custom.blockVersion = meta.version
          return custom
        })
      }
    }
  }, [sourceBlockId, rootNodeId, fetchBlockById, parseSerialized, setCustom, addNodeTree, handleClean])

  return handleUpdate
}

export default define('template-block', TemplateBlockPreview)
  .name('Template Block')
  .build()
